import React, { forwardRef } from "react"
import PropTypes from "prop-types"
import { Box, Table, TableBody, TableHead, TableRow, TableSortLabel, useMediaQuery, useTheme } from "@mui/material"
import { TableVirtuoso } from "react-virtuoso"
import { channelListHeader, getComparator, stableSort } from "./utils/helper"
import { checkBoxStyle } from "./list"
import { VSTableBodyCellStyled, VSTableCellStyledForFunction, VSTableHeaderCellStyled, VSTableRowStyled } from "./utils/vsTableStyled"
import notificationIcon from "../../../assets/images/alarmIcon/notification-icon.svg"
import EditIcon from "../../../assets/images/alarmIcon/edit-icon.svg"
import { defaultChannelGroups } from "./channelPage"
import SortIcon from "../../../assets/images/alarmIcon/sort-icon.svg";
import SortIconPrimary from "../../../assets/images/alarmIcon/sort-icon-primary.svg";
import ResetIcon from "../../../assets/images/alarmIcon/reset-icon.svg";
import ResetIconPrimary from "../../../assets/images/alarmIcon/reset-icon-primary.svg";
import constants from "./utils/constants"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

export const VirtualScrollTable = ({
  TableHeader, 
  displayData, 
  selectedRow, 
  selectedChannels,
  selectedChannelGroup,
  isExistingThresholdList,
  order,
  orderBy,
  updateSelectedChannels,
  openModal,
  handleRequestSort,
  isDisplaySortDialog,
  setIsDisplaySortDialog,
  ResetSort,
  classes,
  i18n,
}) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('md'));
  const isXsDown = useMediaQuery(theme.breakpoints.down('sm'));
  const VirtuosoTableComponents = {
    Table: (props) => ( <Table style={{borderCollapse: "collapse"}} {...props}/>),
    TableHead: forwardRef((props, ref) => (<TableHead {...props} ref={ref}/>)),
    TableRow,
    TableBody: forwardRef((props, ref) => (<TableBody {...props} ref={ref}/>)),
  }

  const VSTableHeader = () => (
      <TableRow  style={{height: "38px", color: "white"}}>
        <VSTableHeaderCellStyled />
        {channelListHeader(t, isSmDown)
          .filter((a) => !a.hideOnSmallDevice)
          .map((d, i) => (
            <VSTableHeaderCellStyled
              key={`alarm-operation-table-header-${i}`}
              align={d.align}
              colSpan={d.id === constants.HEADER_ID.VALUE ? 2 : 1}
              sortDirection={(d.sortable && orderBy) === d.id ? order : false}
              onClick={handleRequestSort(d.id, d.sortable)}
              className={d.sortable ? classes.sortableColumnHeader : classes.columnHeader}
              style={{width: d.tabletWidth}}
              sx={{height: "38px", color: "white"}}
            >
              {
                d.id === constants.HEADER_ID.Notification 
                ? (<img src={notificationIcon} />)
                : (
                  <span className={clsx(i18n?.language !== "en-US" && classes.japaneseTableTitle)}>
                    {d.name}
                  </span>
                )
              }
              {orderBy === d.id ? (
                <span className={classes.visuallyHidden}>
                  {order === constants.ORDER.DESC ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
              {d.sortable && (
                <TableSortLabel
                  slotProps={{icon: {sx: {color: "white !important"}}}}
                  active={orderBy === d.id}
                  direction={orderBy === d.id ? order : "asc"}
                />
              )}
            </VSTableHeaderCellStyled>
          ))}
        {!isXsDown && (
          <VSTableCellStyledForFunction
            sx={{ maxWidth: "30px", width: "30px", backgroundColor: "#2c3a48" }}
            onClick={() => {
              setIsDisplaySortDialog(!isDisplaySortDialog);
            }}
          >
            <img
              src={order === "desc" && orderBy === "chNo" ? SortIcon : SortIconPrimary}
              className={`${classes.controlRow} ${classes.columnHeaderSort}`}
            />
          </VSTableCellStyledForFunction>
        )}
        {!isXsDown && (
          <VSTableCellStyledForFunction
            sx={{ maxWidth: "30px", width: "30px", backgroundColor: "#2c3a48" }}
            onClick={ResetSort}
          >
            <img
              src={order === "desc" && orderBy === "chNo" ? ResetIcon : ResetIconPrimary}
              className={`${classes.controlRow} ${classes.columnHeaderSort}`}
            />
          </VSTableCellStyledForFunction>
        )}
      </TableRow>
  )

  const VSTableBody = (row, selectedRow, selectedChannels) =>
      (
        <>
          <VSTableBodyCellStyled align="left" sx={checkBoxStyle}>              
            <input
              type="checkbox"
              key={row?.chNo}
              id={row?.chNo}
              value={row?.chNo}
              checked={selectedChannels.includes(String(Number(row?.chNo)))}
              onChange={(event) => {
                updateSelectedChannels(row?.chNo, row, event.target.checked);
              }}
            />
          </VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="left">{row.chNo}</VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="left">{row.chName}</VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="center" style={{ paddingRight: "0" }}>
            {row.inputData}
          </VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="center" style={{ paddingLeft: "0" }}>
            {row.unit}
          </VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="center" style={{ paddingLeft: "0" }}>
            {row.chStatus}
          </VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="center">
            {isExistingThresholdList.includes(String(parseInt(row?.chNo))) && <img src={notificationIcon} />}
          </VSTableBodyCellStyled>
          {
            selectedChannelGroup?.id !== defaultChannelGroups[0].id && (
              <VSTableBodyCellStyled align="center"></VSTableBodyCellStyled>
            )
          }
          <VSTableBodyCellStyled align="center" style={{ paddingLeft: "0" }}>
            <Box
              component="img"
              src={EditIcon}
              sx={{ 
                cursor: "pointer",       
                width: "20px",
                height: "20px",
                backgroundColor: "rgba(0, 0, 0, 0)",
                border: "1px solid #000",
                borderRadius: "50%"
              }}
              onClick={() => {
                openModal(row);
              }}
            />
          </VSTableBodyCellStyled>
          <VSTableBodyCellStyled align="center" style={{ paddingLeft: "0" }}></VSTableBodyCellStyled>
        {/* </TableRowStyled> */}
        </>
      )

  return (
    <TableVirtuoso
      style={{width: "100%", height: "100%"}}
      totalCount={displayData.length}
      data={stableSort(displayData, getComparator(order, orderBy, isExistingThresholdList))}
      components={VirtuosoTableComponents}
      fixedHeaderContent={VSTableHeader}
      itemContent={(_index, row) => (   
        VSTableBody(row, selectedRow, selectedChannels)
      )}
    />
  )
}

VirtualScrollTable.propTypes = {
  TableHeader: PropTypes.element,
  displayData:  PropTypes.array,
  selectedRow: PropTypes.string,
  selectedChannels: PropTypes.array,
  selectedChannelGroup: PropTypes.object,
  isExistingThresholdList: PropTypes.array,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  updateSelectedChannels: PropTypes.func,
  openModal: PropTypes.func,
  handleRequestSort: PropTypes.func,
  isDisplaySortDialog: PropTypes.bool,
  setIsDisplaySortDialog: PropTypes.func,
  ResetSort: PropTypes.func,
  classes: PropTypes.object,
  i18n: PropTypes.object,
}